/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { isElementVisible } from '@squiz/dxp-console-support';
import {
  CUSTOM_ICON_FOLDER_COLOUR,
  ICON_CHECK,
  ICON_CHEVRON_RIGHT,
  SPINNER_TYPE_SMALL,
  SdsIcon,
  Spinner,
} from '@squiz/sds';
import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { useFileStore } from '../../../../Services/file-store';
import { DirectoryViewerItem } from '../../DirectoryViewer.props';
import { DirectoryNodeProps } from './DirectoryNode.props';

export const DirectoryNode = ({
  node,
  isSelected,
  preloadedChildren,
  value,
  viewerRef,
  setValue,
}: DirectoryNodeProps): JSX.Element => {
  const nodeRef = useRef<HTMLDivElement>(null);

  // State
  const [isOpen, setIsOpen] = useState(!!preloadedChildren);
  const [empty, setEmpty] = useState(false);
  const [loading, setLoading] = useState(false);
  const [children, setChildren] = useState<Array<DirectoryViewerItem>>(preloadedChildren ?? []);

  // Services
  const { listDirectories } = useFileStore();

  const isRoot = node.path === '/';
  const handleKeyboardEvent = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter') {
      handleOpenNode();
    }
    if (e.code === 'Space') {
      e.preventDefault();
      setValue(node.path);
    }
  };

  const handleOpenNode = async (): Promise<void> => {
    if (isRoot) {
      return;
    }
    if (isOpen) {
      setIsOpen(false);
      setChildren([]);
      return;
    }

    setLoading(true);
    setValue(null);

    const directories = (
      await listDirectories({
        source: node.path,
      })
    ).data;

    if (!directories.length) {
      setLoading(false);
      return setEmpty(true);
    }

    setIsOpen(true);
    setChildren(directories);
    setLoading(false);
  };

  // Scroll to the node if it is selected, but out of view
  useEffect(() => {
    const viewerDiv = viewerRef.current;
    const nodeDiv = nodeRef.current;

    if (isSelected && nodeDiv && viewerDiv) {
      const isVisible = isElementVisible(nodeDiv, viewerDiv);

      if (!isVisible) {
        nodeDiv.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [isSelected, nodeRef, viewerRef]);

  const nodeName = node.path.replace(/\/$/, '').split('/').pop();
  return (
    <div>
      <div
        className={clsx('directory-viewer__node', {
          ['directory-viewer__node--empty']: empty,
          ['directory-viewer__node--open']: isOpen,
          ['directory-viewer__node--root']: isRoot,
          ['directory-viewer__node--selected']: isSelected,
        })}
        data-path={node.path}
        onClick={() => setValue(node.path)}
        onDoubleClick={() => handleOpenNode()}
        onKeyDown={handleKeyboardEvent}
        ref={nodeRef}
        role="button"
        tabIndex={0}
      >
        {!isRoot && <SdsIcon classes="directory-viewer__node-chevron-icon" svg={ICON_CHEVRON_RIGHT} />}
        <span className="directory-viewer__node-folder-icon">{CUSTOM_ICON_FOLDER_COLOUR}</span>
        <span className="directory-viewer__node-name">{isRoot ? 'Root directory' : nodeName}</span>
        {isSelected && <span className="directory-viewer__node-selected-indicator">{ICON_CHECK}</span>}
      </div>
      {loading && (
        <div className="directory-viewer__node-loader">
          <Spinner classes="directory-viewer__node-loader" type={SPINNER_TYPE_SMALL} />
        </div>
      )}
      {!loading && (
        <div className="directory-viewer__node--child">
          {children.map((child) => (
            <DirectoryNode
              isSelected={value === child.path}
              key={child.path}
              node={child}
              preloadedChildren={child.children}
              setValue={setValue}
              value={value}
              viewerRef={viewerRef}
            />
          ))}
        </div>
      )}
    </div>
  );
};
